import { useId } from "react";
import type { ServedQuestion } from "@patent-studio/api-types";
import { CircleHelp } from "lucide-react";
import { FoldingCard } from "@/components/patterns/FoldingCard";
import { Button } from "@/components/ui/button";
import { useDisclosure } from "@/components/ui/Disclosure";
import { IconTile } from "@/components/ui/IconTile";
import { MicroLabel } from "@/components/ui/MicroLabel";
import { Note } from "@/components/ui/Note";
import { StatusPill } from "@/components/ui/StatusPill";
import { useQuestionWrites, useQuestions } from "@/hooks/use-questions";
import { plural } from "@/lib/plural";
import { refusalDetail } from "@/services/api-client";
import { ASKED_AT, askedAt, unresolved } from "../../questions/asked-at";
import { QuestionThread } from "../../questions/QuestionCard";

/** The questions still open, in groups by where Praxis asked them, in the stages' order. */
function grouped(questions: readonly ServedQuestion[]) {
  const open = questions.filter(unresolved);
  const order = [...new Set(ASKED_AT.map(([, label]) => label)), "Other"];
  return order
    .map((label) => ({
      label,
      questions: open.filter((q) => askedAt(q) === label),
    }))
    .filter((group) => group.questions.length > 0);
}

/** One stage's open questions, answered in the thread and saved together. */
function Group({
  taskId,
  label,
  questions,
}: {
  taskId: string;
  label: string;
  questions: ServedQuestion[];
}) {
  const id = useId();
  const writes = useQuestionWrites(taskId);
  const replied = questions.filter((q) => Boolean(q.draft));
  return (
    <div
      role="group"
      aria-labelledby={id}
      className="mt-4 border-t border-border pt-3.5"
    >
      <MicroLabel as="h3" id={id} size="sm" className="mb-2.5">
        {label}
      </MicroLabel>
      <QuestionThread taskId={taskId} asked={questions} writes={writes} panel />
      {replied.length === questions.length && (
        <div className="mt-3 flex justify-end">
          <Button
            disabled={writes.submit.isPending}
            onClick={() => {
              writes.submit.mutate(replied.map((q) => q.question_id));
            }}
          >
            Save answers
          </Button>
        </div>
      )}
      {writes.submit.isError && (
        <Note tone="destructive" role="alert" className="mt-2 text-right">
          {refusalDetail(
            writes.submit.error,
            "Your answers could not be saved. Try again.",
          )}
        </Note>
      )}
    </div>
  );
}

/**
 * "Unanswered questions from Praxis", folded, with the count: the questions
 * still open, answerable here (lane L decision 37). Each group saves its
 * answers together, which moves the open items the review serves.
 */
export function QuestionsCard({
  taskId,
  count,
}: {
  taskId: string;
  /** How many questions the review counts as still open. */
  count: number;
}) {
  const fold = useDisclosure(false);
  const store = useQuestions(taskId);
  const groups = grouped(store.data?.questions ?? []);
  return (
    <FoldingCard
      fold={fold}
      className="mt-4"
      lead={
        <IconTile size="md" tone="info">
          <CircleHelp />
        </IconTile>
      }
      title={
        <span className="text-[15px] font-bold">
          Unanswered questions from Praxis
        </span>
      }
      meta={
        <StatusPill tone="neutral" plain>
          {plural(count, "question")}
        </StatusPill>
      }
    >
      <p className="pt-3 text-[12.5px] text-muted-foreground">
        You skipped these earlier. Answering them helps Praxis before it
        generates the draft.
      </p>
      {groups.map((group) => (
        <Group
          key={group.label}
          taskId={taskId}
          label={group.label}
          questions={group.questions}
        />
      ))}
    </FoldingCard>
  );
}
